import { useCallback, useEffect, useState } from 'react';
import {
  deleteDynamicFormsClient,
  deleteDynamicFormsForm,
  deleteDynamicFormsTemplate,
  loadDynamicFormsData,
  saveDynamicFormsClient,
  saveDynamicFormsForm,
  saveDynamicFormsTemplate,
} from '../api';

const upsertItem = (items, nextItem) => {
  const existingIndex = items.findIndex((item) => item.id === nextItem.id);
  if (existingIndex === -1) {
    return [...items, nextItem];
  }

  const nextItems = [...items];
  nextItems[existingIndex] = nextItem;
  return nextItems;
};

export function useDynamicFormsData(user) {
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const requireUser = useCallback(() => {
    if (!user) {
      throw new Error('Authentication is required to load dynamic forms.');
    }
  }, [user]);

  const reload = useCallback(async () => {
    requireUser();
    setLoading(true);
    setError(null);

    try {
      const data = await loadDynamicFormsData(user);
      setClients(data.clients || []);
      setTemplates(data.templates || []);
      setForms(data.forms || []);
      return data;
    } catch (loadError) {
      setError(loadError);
      throw loadError;
    } finally {
      setLoading(false);
    }
  }, [requireUser, user]);

  useEffect(() => {
    let active = true;

    if (!user) {
      setClients([]);
      setTemplates([]);
      setForms([]);
      setLoading(false);
      setError(new Error('Authentication is required to load dynamic forms.'));
      return undefined;
    }

    setLoading(true);
    setError(null);

    loadDynamicFormsData(user)
      .then((data) => {
        if (!active) {
          return;
        }
        setClients(data.clients || []);
        setTemplates(data.templates || []);
        setForms(data.forms || []);
      })
      .catch((loadError) => {
        if (!active) {
          return;
        }
        setError(loadError);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [user]);

  const saveClient = useCallback(async (payload) => {
    requireUser();
    const savedClient = await saveDynamicFormsClient(user, payload);
    setClients((previousClients) => upsertItem(previousClients, savedClient));
    return savedClient;
  }, [requireUser, user]);

  const deleteClient = useCallback(async (clientId) => {
    requireUser();
    const deletedClient = await deleteDynamicFormsClient(user, clientId);
    setClients((previousClients) => previousClients.filter((client) => client.id !== clientId));
    setForms((previousForms) => previousForms.filter((form) => form.clientId !== clientId));
    return deletedClient;
  }, [requireUser, user]);

  const saveTemplate = useCallback(async (payload) => {
    requireUser();
    const savedTemplate = await saveDynamicFormsTemplate(user, payload);
    setTemplates((previousTemplates) => upsertItem(previousTemplates, savedTemplate));
    return savedTemplate;
  }, [requireUser, user]);

  const deleteTemplate = useCallback(async (templateId) => {
    requireUser();
    const deletedTemplate = await deleteDynamicFormsTemplate(user, templateId);
    setTemplates((previousTemplates) => previousTemplates.filter((template) => template.id !== templateId));
    setForms((previousForms) => previousForms.filter((form) => form.templateId !== templateId));
    return deletedTemplate;
  }, [requireUser, user]);

  const saveForm = useCallback(async (payload) => {
    requireUser();
    const savedForm = await saveDynamicFormsForm(user, payload);
    setForms((previousForms) => upsertItem(previousForms, savedForm));
    return savedForm;
  }, [requireUser, user]);

  const deleteForm = useCallback(async (formId) => {
    requireUser();
    const deletedForm = await deleteDynamicFormsForm(user, formId);
    setForms((previousForms) => previousForms.filter((form) => form.id !== formId));
    return deletedForm;
  }, [requireUser, user]);

  return {
    clients,
    templates,
    forms,
    loading,
    error,
    reload,
    saveClient,
    deleteClient,
    saveTemplate,
    deleteTemplate,
    saveForm,
    deleteForm,
  };
}
